import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { NotificationsInternesService } from '../../../core/services/notifications-internes.service';
import { NotificationInterne } from '../../../core/models/notification-interne.model';
import { AuthService } from '../../../core/services/auth.service';
import { ROLES } from '../../../core/constants/roles.constants';

@Component({
  selector: 'app-notification-informer',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './notification-informer.component.html',
  styleUrls: ['./notification-informer.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NotificationInformerComponent implements OnInit, OnDestroy {
  visible = false;
  notification: NotificationInterne | null = null;
  subtitle = '';
  toneColor = '#3B6EA5';

  private toastTimer: any = null;
  private readonly subs = new Subscription();

  private isOnDashboard = false;

  get estSuiviEtablissement(): boolean {
    return this.notification?.donnees?.audience === 'GESTIONNAIRE';
  }

  constructor(
    private readonly notificationsService: NotificationsInternesService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    // Indispensable : sans le composant cloche sur les pages non-dashboard,
    // on initialiserait quand même la cloche pour écouter les nouveaux events.
    this.notificationsService.init();

    this.isOnDashboard = this.router.url?.startsWith('/dashboard') ?? false;

    this.subs.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => {
          this.isOnDashboard = this.router.url?.startsWith('/dashboard') ?? false;
        })
    );

    this.subs.add(
      this.notificationsService.nouvelleNotification$.subscribe((n) => {
        if (this.isOnDashboard) {
          return;
        }
        // Copies de suivi gestionnaire : visibles uniquement dans la cloche du dashboard.
        if (n.donnees?.audience === 'GESTIONNAIRE') {
          return;
        }
        // Messages individuels : badge sur l'icône Messages, pas de toast cloche.
        if (n.evenement === 'MESSAGE_FAMILLE_RECU') {
          return;
        }
        this.showToast(n);
      })
    );
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.subs.unsubscribe();
  }

  close(event?: Event): void {
    event?.stopPropagation();
    this.visible = false;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  openFromToast(): void {
    if (!this.notification) {
      return;
    }

    const notificationId = this.notification.id;
    this.close();

    // Clic sur le toast = on considère l'info lue.
    this.notificationsService
      .marquerLue(notificationId)
      .subscribe({ error: () => undefined });

    const roleName = this.authService.getCurrentUser()?.role?.nom ?? null;
    const dashboardPath = this.dashboardPathForRole(roleName);

    // La demande est posée après la navigation : la cloche du dashboard est déjà
    // abonnée, et rien ne reste en attente si la navigation échoue.
    this.router
      .navigate([dashboardPath])
      .then(navigue => {
        if (navigue !== false) {
          this.notificationsService.demanderOuverturePanneau('toutes');
        }
      })
      .catch(() => undefined);
  }

  private showToast(notification: NotificationInterne): void {
    this.notification = notification;
    this.subtitle = this.buildSubtitle(notification);
    this.toneColor = this.toneColorFor(notification);
    this.visible = true;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    // Auto-close après quelques secondes.
    this.toastTimer = setTimeout(() => {
      this.visible = false;
      this.toastTimer = null;
    }, 6500);
  }

  private buildSubtitle(n: NotificationInterne): string {
    const message = (n.message || '').replace(/\s+/g, ' ').trim();
    if (message) {
      return message;
    }
    return n.donnees?.dateHeureLibelle ?? '';
  }

  private toneColorFor(notification: NotificationInterne): string {
    const evenement = notification.evenement;
    if (evenement === 'RAPPORT_QUOTIDIEN') {
      const nb = notification.donnees?.nbAlertes ?? 0;
      return nb > 0 ? '#f39c12' : '#27ae60';
    }

    switch (evenement) {
      case 'PLANNING_CREE':
      case 'PLANNING_REALISE':
      case 'CONSENTEMENT_ACCEPTE':
      case 'CONSENTEMENT_PROMU':
      case 'AUTORISATION_ACCORDEE':
      case 'PERSONNE_CONFIANCE_DESIGNEE':
      case 'DOCUMENT_PARTAGE':
      case 'MEDIA_PARTAGE':
      case 'CAMPAGNE_ACTIVEE':
      case 'MESSAGE_FAMILLE_RECU':
      case 'NOTICE_PUBLIEE':
      case 'REPROVISIONING_SUCCES':
      case 'USER_RETENTION_HOLD':
        return '#27ae60';
      case 'PLANNING_MODIFIE':
      case 'CONSENTEMENT_DEMANDE':
      case 'CONSENTEMENT_EXPIRE':
      case 'CAMPAGNE_CLOTUREE':
      case 'REMOTE_ACTION_EXPIRED':
      case 'FAMILLE_INACTIVE':
      case 'FAMILLE_RELANCE':
      case 'USER_INACTIVITY_PREAVIS':
        return '#f39c12';
      case 'PLANNING_SUPPRIME':
      case 'PLANNING_ANNULE_INTERVENANT':
      case 'PLANNING_ANNULE_ANIMATEUR':
      case 'CONSENTEMENT_REFUSE':
      case 'AUTORISATION_REVOQUEE':
      case 'PERSONNE_CONFIANCE_RETIREE':
      case 'REMOTE_ACTION_FAILED':
      case 'REPROVISIONING_ECHEC':
      case 'USER_DEACTIVATED_INACTIVITY':
        return '#e74c3c';
      default:
        return '#3B6EA5';
    }
  }

  private dashboardPathForRole(roleName: string | null): string {
    if (roleName === ROLES.FAMILLE) {
      return '/dashboard-famille';
    }
    if (roleName === ROLES.ANIMATEUR) {
      return '/dashboard-animateur';
    }
    if (roleName === ROLES.PODOLOGUE || roleName === ROLES.COIFFEUR) {
      return '/dashboard-externe';
    }
    if (roleName === ROLES.EHPAD) {
      return '/dashboard-gestionnaire';
    }
    if (roleName === ROLES.TECH) {
      return '/dashboard-technicien';
    }
    return '/dashboard';
  }
}

